import React from "react";
import { Tag, Tooltip } from "antd";
import dayjs, { Dayjs } from "dayjs";
import type { ColumnsType } from "antd/es/table";
import type { SorterResult } from "antd/es/table/interface";
import {
	ClientCountryTotals,
	SortDirection,
} from "../../../api/client-reports";

/**
 * Wire format for every date parameter on these endpoints: local wall-clock "YYYY-MM-DD" with no
 * timezone suffix. An ISO "…Z" value would be converted by the API and shift the window by a day.
 */
export const toWireDate = (value: Dayjs) => value.format("YYYY-MM-DD");

/** Date only — these reports deal in calendar days, so the time part is noise. */
export const formatDate = (value: string | null | undefined) => {
	if (!value) {
		return "—";
	}
	const date = dayjs(value);
	return date.isValid() ? date.format("DD/MM/YYYY") : value;
};

/** Date and time, for the one place it matters: the exact moment of the last order. */
export const formatDateTime = (value: string | null | undefined) => {
	if (!value) {
		return "—";
	}
	const date = dayjs(value);
	return date.isValid() ? date.format("DD/MM/YYYY HH:mm") : value;
};

export const formatNumber = (value: number | null | undefined) =>
	typeof value === "number" ? value.toLocaleString() : "—";

/**
 * Colours the inactivity gap so a call list can be triaged at a glance rather than read row by
 * row. The thresholds are presentation only — the API applies no such banding.
 */
export const daysTagColor = (days: number | null | undefined) => {
	if (typeof days !== "number") {
		return "default";
	}
	if (days >= 90) {
		return "red";
	}
	if (days >= 30) {
		return "orange";
	}
	return "green";
};

/**
 * Maps AG-free antd sort state onto the API's `sortBy`/`sortDirection` pair.
 *
 * `columnKey` IS the API's sort key — every sortable column in this module is keyed with the
 * whitelisted name the server expects, so no lookup table can fall out of date. Clearing the sort
 * returns undefined for both, which makes the API apply its own default.
 */
export const toApiSort = <T,>(
	sorter: SorterResult<T>
): { sortBy?: string; sortDirection?: SortDirection } => {
	if (!sorter.order) {
		return { sortBy: undefined, sortDirection: undefined };
	}
	return {
		sortBy: sorter.columnKey as string,
		sortDirection: sorter.order === "ascend" ? "asc" : "desc",
	};
};

export interface IdentityColumnLabels {
	code: string;
	name: string;
	business: string;
	phone: string;
	businessPhone: string;
	clientSince: string;
	totalOrders: string;
	countrySplit: string;
	unknownCountryHint: string;
}

/**
 * Identity columns shared by reports 1 and 2 — everything that describes WHO the client is, as
 * opposed to what their dates say. Kept in one place so the two reports cannot drift apart.
 */
export const identityColumns = <T extends ClientCountryTotals>(
	labels: IdentityColumnLabels
): ColumnsType<T> => [
	{
		title: labels.code,
		dataIndex: "uniqueSellerIdentifier",
		key: "uniqueSellerIdentifier",
		width: 110,
		fixed: "left",
		render: (code: string | null, row) => (
			<span className="flex flex-col">
				<span className="font-medium">{code ?? "—"}</span>
				<span className="text-xs text-gray-400">{row.sellerId}</span>
			</span>
		),
	},
	{
		// `clientName` is the API's own sort key, so the column key doubles as the value sent
		// back on sort. Every sortable column in this module follows that rule — it is what
		// keeps the whitelist on the server and the grid here from drifting apart.
		title: labels.name,
		key: "clientName",
		width: 170,
		sorter: true,
		render: (_: unknown, row) => row.nameAndSurname?.trim() || "—",
	},
	{
		title: labels.business,
		dataIndex: "businessName",
		key: "businessName",
		width: 170,
		render: (business: string | null) => business || "—",
	},
	{
		title: labels.phone,
		dataIndex: "phone",
		key: "phone",
		width: 140,
		render: (phone: string | null) => phone || "—",
	},
	{
		title: labels.businessPhone,
		dataIndex: "businessPhone",
		key: "businessPhone",
		width: 140,
		render: (phone: string | null) => phone || "—",
	},
];

/**
 * Lifetime volume plus the destination-country split.
 *
 * The split renders as tags rather than the four separate numeric columns the Excel export uses:
 * on screen most clients ship to one country, so four mostly-zero columns cost a lot of width to
 * say very little. Zero buckets are hidden, and the "no country" bucket appears in red only when
 * it is non-zero — that is the case worth noticing, since it means an order's section does not
 * resolve to a country.
 */
export const countryTotalsColumns = <T extends ClientCountryTotals>(
	labels: IdentityColumnLabels
): ColumnsType<T> => [
	{
		title: labels.totalOrders,
		dataIndex: "totalOrders",
		key: "totalOrders",
		align: "right",
		width: 110,
		sorter: true,
		render: (total: number) => (
			<span className="font-medium">{formatNumber(total)}</span>
		),
	},
	{
		title: labels.countrySplit,
		key: "countrySplit",
		width: 190,
		render: (_: unknown, row) => (
			<span className="flex flex-wrap gap-1">
				{row.ordersKs > 0 && (
					<Tag color="blue">KS {formatNumber(row.ordersKs)}</Tag>
				)}
				{row.ordersAl > 0 && (
					<Tag color="geekblue">AL {formatNumber(row.ordersAl)}</Tag>
				)}
				{row.ordersMk > 0 && (
					<Tag color="purple">NMK {formatNumber(row.ordersMk)}</Tag>
				)}
				{row.ordersUnknownCountry > 0 && (
					<Tooltip title={labels.unknownCountryHint}>
						<Tag color="red">
							? {formatNumber(row.ordersUnknownCountry)}
						</Tag>
					</Tooltip>
				)}
				{row.totalOrders === 0 && (
					<span className="text-gray-400">—</span>
				)}
			</span>
		),
	},
	{
		title: labels.clientSince,
		dataIndex: "clientSince",
		key: "clientSince",
		width: 120,
		sorter: true,
		render: (value: string) => formatDate(value),
	},
];
